import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Users, CreditCard, BarChart3, Settings, Palette, 
  Shield, Headphones, TestTube, FileText, Database, Mail, DollarSign,
  ChevronRight, RefreshCw, Search, Edit, Trash2,
  Check, X, AlertTriangle, Lock, Unlock, Key, Download,
  Filter, Sparkles, Sun, Home,
  Upload, GripVertical, ToggleLeft, ToggleRight, Box, LogIn
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from './store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');
const APP_URL = process.env.REACT_APP_APP_URL || '';

const ROLE_CONFIG = {
  superuser: { icon: Shield, color: '#C9A84C', label: 'Super Utilisateur' },
  admin: { icon: Settings, color: '#0E7C7B', label: 'Administrateur' },
  finance: { icon: CreditCard, color: '#22C55E', label: 'Finance' },
  hotline: { icon: Headphones, color: '#3B82F6', label: 'Hotline' },
  tester: { icon: TestTube, color: '#A855F7', label: 'Testeur' },
  trial: { icon: AlertTriangle, color: '#F59E0B', label: 'Essai' },
  normal: { icon: Users, color: '#64748B', label: 'Normal' }
};

const ADMIN_SECTIONS = [
  { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3, roles: ['superuser', 'admin'], available: true },
  { id: 'users', label: 'Utilisateurs', icon: Users, roles: ['superuser', 'admin', 'hotline'], available: true },
  { id: 'objects', label: 'Objets', icon: Palette, roles: ['superuser'], available: true },
  { id: 'prompts', label: 'Prompts IA', icon: Sparkles, roles: ['superuser'], available: true },
  { id: 'generations', label: 'Générations IA', icon: Sparkles, roles: ['superuser', 'admin'], available: true },
  { id: 'finance', label: 'Finance', icon: CreditCard, roles: ['superuser', 'finance'], available: true },
  { id: 'catalogs', label: 'Catalogues', icon: Palette, roles: ['superuser', 'admin'], available: true },
  { id: 'config', label: 'Configuration', icon: Settings, roles: ['superuser'], available: true },
  { id: 'adminlogs', label: 'Logs Admin', icon: FileText, roles: ['superuser'], available: true },
  { id: 'emails', label: 'Templates Email', icon: Mail, roles: ['superuser'], available: false },
  { id: 'logs', label: 'Logs IA', icon: FileText, roles: ['superuser', 'admin'], available: true },
  { id: 'aicosts', label: 'Coûts IA', icon: DollarSign, roles: ['superuser', 'admin'], available: true },
  { id: 'beta', label: 'Fonctions Beta', icon: TestTube, roles: ['superuser', 'tester'], available: false },
];

export default function AdminPage() {
  const { user, token } = useAuthStore();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [generations, setGenerations] = useState({ generations: [], total: 0 });
  const [configSchema, setConfigSchema] = useState(null);
  const [configValues, setConfigValues] = useState({});
  const [objectsConfig, setObjectsConfig] = useState({});
  const [promptsConfig, setPromptsConfig] = useState({ prompts: {}, schema: {} });
  const [settings, setSettings] = useState(null);
  const [costSummary, setCostSummary] = useState(null);
  const [costsByModel, setCostsByModel] = useState([]);
  const [costsByUser, setCostsByUser] = useState([]);
  const [costsByMonth, setCostsByMonth] = useState([]);
  const [costsDetail, setCostsDetail] = useState({ records: [], total: 0 });
  const [costPricingConfig, setCostPricingConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const userRoles = user?.roles || [];
  const hasAccess = userRoles.includes('superuser') || 
                    userRoles.includes('admin') || 
                    userRoles.includes('finance') || 
                    userRoles.includes('hotline') ||
                    userRoles.includes('tester');

  const canAccessSection = (section) => {
    if (userRoles.includes('superuser')) return true;
    return section.roles.some(role => userRoles.includes(role));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      await useAuthStore.getState().login(loginEmail, loginPassword);
    } catch {
      setLoginError('Email ou mot de passe invalide');
    } finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = { 'Authorization': `Bearer ${token}` };

        switch (activeSection) {
          case 'dashboard': {
            const statsRes = await fetch(`${API_URL}/api/admin/stats`, { headers, credentials: 'include' });
            if (statsRes.ok) setStats(await statsRes.json());
            break;
          }
          case 'users': {
            const usersRes = await fetch(`${API_URL}/api/admin/users`, { headers, credentials: 'include' });
            if (usersRes.ok) setUsers(await usersRes.json());
            break;
          }
          case 'finance': {
            const txRes = await fetch(`${API_URL}/api/admin/finance/transactions`, { headers, credentials: 'include' });
            if (txRes.ok) setTransactions(await txRes.json());
            break;
          }
          case 'catalogs': {
            const catRes = await fetch(`${API_URL}/api/admin/catalogs`, { headers, credentials: 'include' });
            if (catRes.ok) setCatalogs(await catRes.json());
            break;
          }
          case 'settings': {
            const setRes = await fetch(`${API_URL}/api/admin/settings`, { headers, credentials: 'include' });
            if (setRes.ok) setSettings(await setRes.json());
            break;
          }
          case 'logs': {
            const logsRes = await fetch(`${API_URL}/api/admin/logs/ai`, { headers, credentials: 'include' });
            if (logsRes.ok) setLogs(await logsRes.json());
            break;
          }
          case 'aicosts': {
            const [summaryRes, modelRes, userRes, monthRes, detailRes, configRes] = await Promise.all([
              fetch(`${API_URL}/api/admin/costs/summary`, { headers, credentials: 'include' }),
              fetch(`${API_URL}/api/admin/costs/by-model`, { headers, credentials: 'include' }),
              fetch(`${API_URL}/api/admin/costs/by-user`, { headers, credentials: 'include' }),
              fetch(`${API_URL}/api/admin/costs/by-month`, { headers, credentials: 'include' }),
              fetch(`${API_URL}/api/admin/costs/detail?limit=50`, { headers, credentials: 'include' }),
              fetch(`${API_URL}/api/admin/costs/config`, { headers, credentials: 'include' }),
            ]);
            if (summaryRes.ok) setCostSummary(await summaryRes.json());
            if (modelRes.ok) setCostsByModel(await modelRes.json());
            if (userRes.ok) setCostsByUser(await userRes.json());
            if (monthRes.ok) setCostsByMonth(await monthRes.json());
            if (detailRes.ok) setCostsDetail(await detailRes.json());
            if (configRes.ok) setCostPricingConfig(await configRes.json());
            break;
          }
          case 'adminlogs': {
            const adminLogsRes = await fetch(`${API_URL}/api/admin/logs/actions`, { headers, credentials: 'include' });
            if (adminLogsRes.ok) setAdminLogs(await adminLogsRes.json());
            break;
          }
          case 'generations': {
            const genRes = await fetch(`${API_URL}/api/admin/generations?limit=50`, { headers, credentials: 'include' });
            if (genRes.ok) setGenerations(await genRes.json());
            break;
          }
          case 'config': {
            const [schemaRes, valuesRes] = await Promise.all([
              fetch(`${API_URL}/api/admin/config/schema`, { headers, credentials: 'include' }),
              fetch(`${API_URL}/api/admin/config`, { headers, credentials: 'include' })
            ]);
            if (schemaRes.ok) setConfigSchema(await schemaRes.json());
            if (valuesRes.ok) {
              const data = await valuesRes.json();
              setConfigValues(data.config || {});
            }
            break;
          }
          case 'objects': {
            const objRes = await fetch(`${API_URL}/api/admin/objects`, { headers, credentials: 'include' });
            if (objRes.ok) {
              const data = await objRes.json();
              setObjectsConfig(data.objects || {});
            }
            break;
          }
          case 'prompts': {
            const promptsRes = await fetch(`${API_URL}/api/admin/prompts`, { headers, credentials: 'include' });
            if (promptsRes.ok) {
              const data = await promptsRes.json();
              setPromptsConfig(data);
            }
            break;
          }
          default:
            break;
        }
      } catch (error) {
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeSection, token]);

  const updateUserRoles = async (userId, newRoles) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ roles: newRoles })
      });
      if (res.ok) {
        toast.success('Rôles mis à jour');
        const usersRes = await fetch(`${API_URL}/api/admin/users`, { 
          headers: { 'Authorization': `Bearer ${token}` }, 
          credentials: 'include' 
        });
        if (usersRes.ok) setUsers(await usersRes.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const updateUserCredits = async (userId, credits) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/credits?credits=${credits}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Crédits mis à jour');
        setUsers(users.map(u => u.user_id === userId ? { ...u, credits } : u));
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!token) {
    return (
      <div className="h-full w-full bg-background flex items-center justify-center p-4">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="font-display text-teal-400 font-bold text-2xl">SoView</div>
            <div className="text-white/40 text-sm mt-1">Administration</div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="admin-login-email" className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Email</label>
              <input
                id="admin-login-email"
                name="email"
                type="email"
                autoComplete="username"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-teal-500/50"
                placeholder="dev@soviewfx.fr"
                required
              />
            </div>
            <div>
              <label htmlFor="admin-login-password" className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Mot de passe</label>
              <input
                id="admin-login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-teal-500/50"
                placeholder="••••••••"
                required
              />
            </div>
            {loginError && (
              <p className="text-red-400 text-xs">{loginError}</p>
            )}
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full flex items-center justify-center gap-2 bg-teal-600 text-white rounded-lg px-4 py-2.5 text-sm font-bold disabled:opacity-50"
            >
              <LogIn size={16} />
              {loginLoading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!hasAccess) return null;

  return (
    <div className="h-full flex flex-col bg-background">
      <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0 z-10">
        {APP_URL && (
          <a href={APP_URL} className="p-2 rounded-lg hover:bg-white/5">
            <ArrowLeft size={20} />
          </a>
        )}
        <div className="flex-1">
          <h1 className="font-display font-bold text-lg">Administration</h1>
          <div className="flex gap-1 mt-0.5">
            {userRoles.filter(r => r !== 'normal').map(role => (
              <span 
                key={role} 
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase"
                style={{ backgroundColor: `${ROLE_CONFIG[role]?.color}20`, color: ROLE_CONFIG[role]?.color }}
              >
                {ROLE_CONFIG[role]?.label}
              </span>
            ))}
          </div>
        </div>
        <button 
          onClick={() => window.location.reload()} 
          className="p-2 rounded-lg hover:bg-white/5"
          disabled={loading}
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <nav className="w-56 border-r border-white/5 overflow-y-auto p-2 flex-shrink-0">
          {ADMIN_SECTIONS.map(section => {
            const Icon = section.icon;
            const canAccess = canAccessSection(section);
            const isActive = activeSection === section.id;

            return (
              <button
                key={section.id}
                onClick={() => canAccess && section.available && setActiveSection(section.id)}
                disabled={!canAccess || !section.available}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-left transition-all ${
                  isActive 
                    ? 'bg-secondary/20 text-secondary' 
                    : canAccess && section.available
                      ? 'hover:bg-white/5 text-white'
                      : 'opacity-40 cursor-not-allowed'
                } ${!section.available ? 'border border-dashed border-violet-500/50' : ''}`}
                data-testid={`admin-nav-${section.id}`}
              >
                <Icon size={18} className={!section.available ? 'text-violet-500' : ''} />
                <span className={`text-sm font-medium flex-1 ${!section.available ? 'text-violet-400' : ''}`}>
                  {section.label}
                </span>
                {!section.available && (
                  <span className="text-[9px] text-violet-400 font-bold">SOON</span>
                )}
              </button>
            );
          })}
        </nav>

        <main className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="spinner" />
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {activeSection === 'dashboard' && stats && (
                  <DashboardSection stats={stats} />
                )}

                {activeSection === 'users' && (
                  <UsersSection 
                    users={filteredUsers} 
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    onUpdateRoles={updateUserRoles}
                    onUpdateCredits={updateUserCredits}
                    isSuperuser={userRoles.includes('superuser')}
                    token={token}
                    refreshUsers={async () => {
                      const res = await fetch(`${API_URL}/api/admin/users`, { 
                        headers: { 'Authorization': `Bearer ${token}` }, 
                        credentials: 'include' 
                      });
                      if (res.ok) setUsers(await res.json());
                    }}
                  />
                )}

                {activeSection === 'generations' && (
                  <GenerationsSection 
                    generations={generations}
                    token={token}
                    setGenerations={setGenerations}
                  />
                )}

                {activeSection === 'config' && configSchema && (
                  <ConfigSection 
                    schema={configSchema}
                    values={configValues}
                    setValues={setConfigValues}
                    token={token}
                  />
                )}

                {activeSection === 'objects' && (
                  <ObjectsSection
                    objects={objectsConfig}
                    setObjects={setObjectsConfig}
                    token={token}
                  />
                )}

                {activeSection === 'prompts' && (
                  <PromptsSection
                    promptsData={promptsConfig}
                    setPromptsData={setPromptsConfig}
                    token={token}
                  />
                )}

                {activeSection === 'finance' && (
                  <FinanceSection transactions={transactions} />
                )}

                {activeSection === 'catalogs' && (
                  <CatalogsSection catalogs={catalogs} token={token} setCatalogs={setCatalogs} />
                )}

                {activeSection === 'settings' && (
                  <SettingsSection settings={settings} token={token} setSettings={setSettings} />
                )}

                {activeSection === 'logs' && (
                  <LogsSection logs={logs} />
                )}

                {activeSection === 'adminlogs' && (
                  <AdminLogsSection logs={adminLogs} />
                )}

                {activeSection === 'aicosts' && costSummary && (
                  <AICostsSection
                    summary={costSummary}
                    byModel={costsByModel}
                    byUser={costsByUser}
                    byMonth={costsByMonth}
                    details={costsDetail}
                    pricingConfig={costPricingConfig}
                  />
                )}

                {['ai', 'emails', 'beta'].includes(activeSection) && (
                  <div className="text-center py-20">
                    <div className="w-20 h-20 bg-violet-500/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <TestTube size={40} className="text-violet-500" />
                    </div>
                    <h2 className="text-xl font-bold text-violet-400 mb-2">En développement</h2>
                    <p className="text-muted-foreground">Cette fonctionnalité sera bientôt disponible.</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </main>
      </div>
    </div>
  );
}

function DashboardSection({ stats }) {
  const statCards = [
    { label: 'Utilisateurs', value: stats.total_users, icon: Users, color: '#0E7C7B' },
    { label: 'Utilisateurs payants', value: stats.paid_users, icon: CreditCard, color: '#22C55E' },
    { label: 'Emails vérifiés', value: stats.verified_users, icon: Mail, color: '#3B82F6' },
    { label: 'Projets', value: stats.total_projects, icon: FileText, color: '#A855F7' },
    { label: 'Appels IA', value: stats.total_ai_calls, icon: Database, color: '#F59E0B' },
    { label: 'Revenus', value: `${stats.total_revenue?.toFixed(2) || 0}€`, icon: BarChart3, color: '#C9A84C' },
  ];

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Tableau de bord</h2>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="card p-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <Icon size={20} style={{ color: stat.color }} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="font-bold mb-3">Utilisateurs par rôle</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {Object.entries(stats.users_by_role || {}).map(([role, count]) => {
          const config = ROLE_CONFIG[role];
          const Icon = config?.icon || Users;
          return (
            <div key={role} className="bg-white/5 rounded-lg p-3 flex items-center gap-2">
              <Icon size={16} style={{ color: config?.color }} />
              <span className="text-sm font-medium">{config?.label}</span>
              <span className="ml-auto font-bold">{count}</span>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Inscriptions cette semaine: <span className="text-secondary font-bold">{stats.recent_signups}</span>
      </p>
    </div>
  );
}

function UsersSection({ users, searchQuery, setSearchQuery, onUpdateRoles, onUpdateCredits, isSuperuser, token, refreshUsers }) {
  const [editingUser, setEditingUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const sendResetPassword = async (userId, email) => {
    setActionLoading(`reset-${userId}`);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/send-reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        toast.success(`Email de réinitialisation envoyé à ${email}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  const verifyEmail = async (userId, email) => {
    setActionLoading(`verify-${userId}`);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/verify-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.already_verified) {
          toast.info('Email déjà vérifié');
        } else {
          toast.success(`Email vérifié pour ${email}`);
          refreshUsers();
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleBlock = async (userId, email, isBlocked) => {
    setActionLoading(`block-${userId}`);
    try {
      const endpoint = isBlocked ? 'unblock' : 'block';
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        toast.success(isBlocked ? `${email} débloqué` : `${email} bloqué`);
        refreshUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Utilisateurs ({users.length})</h2>
      </div>

      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Rechercher par email ou nom..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full input pl-10"
          data-testid="user-search-input"
        />
      </div>

      <div className="space-y-2">
        {users.map(user => (
          <div key={user.user_id} className={`card p-3 ${user.blocked ? 'border-red-500/50 bg-red-500/5' : ''}`}>
            <div className="flex items-start gap-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-bold">
                  {user.name?.[0]?.toUpperCase()}
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-bold truncate">{user.name}</p>
                  {user.blocked && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 font-bold">
                      BLOQUÉ
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(user.roles || []).map(role => (
                    <span 
                      key={role}
                      className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                      style={{ 
                        backgroundColor: `${ROLE_CONFIG[role]?.color}20`, 
                        color: ROLE_CONFIG[role]?.color 
                      }}
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>

              <div className="text-right text-xs">
                <p><span className="text-primary font-bold">{user.credits}</span> crédits</p>
                <p className="text-muted-foreground">{user.plan}</p>
                <div className="flex items-center justify-end gap-1 mt-1">
                  {user.email_verified ? (
                    <span className="flex items-center gap-0.5 text-green-500">
                      <Check size={12} />
                      <span className="text-[9px]">Vérifié</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-0.5 text-red-400">
                      <X size={12} />
                      <span className="text-[9px]">Non vérifié</span>
                    </span>
                  )}
                </div>
              </div>

              {isSuperuser && (
                <button
                  onClick={() => setEditingUser(editingUser === user.user_id ? null : user.user_id)}
                  className="p-2 rounded-lg hover:bg-white/10"
                  data-testid={`edit-user-${user.user_id}`}
                >
                  <Edit size={16} />
                </button>
              )}
            </div>

            {editingUser === user.user_id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-3 pt-3 border-t border-white/10"
              >
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => sendResetPassword(user.user_id, user.email)}
                    disabled={actionLoading === `reset-${user.user_id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 text-xs font-medium hover:bg-blue-500/30 disabled:opacity-50"
                    data-testid={`reset-pwd-${user.user_id}`}
                  >
                    <Key size={14} />
                    {actionLoading === `reset-${user.user_id}` ? 'Envoi...' : 'Reset mot de passe'}
                  </button>

                  {!user.email_verified && (
                    <button
                      onClick={() => verifyEmail(user.user_id, user.email)}
                      disabled={actionLoading === `verify-${user.user_id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 text-xs font-medium hover:bg-green-500/30 disabled:opacity-50"
                      data-testid={`verify-email-${user.user_id}`}
                    >
                      <Check size={14} />
                      {actionLoading === `verify-${user.user_id}` ? 'Validation...' : 'Valider email'}
                    </button>
                  )}

                  <button
                    onClick={() => toggleBlock(user.user_id, user.email, user.blocked)}
                    disabled={actionLoading === `block-${user.user_id}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${
                      user.blocked 
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    }`}
                    data-testid={`block-${user.user_id}`}
                  >
                    {user.blocked ? <Unlock size={14} /> : <Lock size={14} />}
                    {actionLoading === `block-${user.user_id}` ? '...' : (user.blocked ? 'Débloquer' : 'Bloquer')}
                  </button>
                </div>

                <p className="text-xs font-bold mb-2">Rôles</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(ROLE_CONFIG).map(([role, config]) => {
                    const hasRole = (user.roles || []).includes(role);
                    return (
                      <button
                        key={role}
                        onClick={() => {
                          const newRoles = hasRole 
                            ? (user.roles || []).filter(r => r !== role)
                            : [...(user.roles || []), role];
                          onUpdateRoles(user.user_id, newRoles);
                        }}
                        className={`text-xs px-2 py-1 rounded-full border ${
                          hasRole 
                            ? 'border-transparent' 
                            : 'border-dashed border-white/20'
                        }`}
                        style={{ 
                          backgroundColor: hasRole ? `${config.color}30` : 'transparent',
                          color: hasRole ? config.color : '#64748B'
                        }}
                      >
                        {config.label}
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs">Crédits:</span>
                  <input
                    type="number"
                    defaultValue={user.credits}
                    className="w-20 bg-white/5 border border-white/10 rounded px-2 py-1 text-sm"
                    onBlur={(e) => onUpdateCredits(user.user_id, parseInt(e.target.value))}
                  />
                </div>
              </motion.div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FinanceSection({ transactions }) {
  const total = transactions.filter(t => t.payment_status === 'completed').reduce((sum, t) => sum + t.amount, 0);

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Finance</h2>

      <div className="card p-4 mb-4">
        <p className="text-sm text-muted-foreground">Revenus totaux</p>
        <p className="text-3xl font-bold text-primary">{total.toFixed(2)}€</p>
      </div>

      <h3 className="font-bold mb-3">Transactions récentes</h3>
      <div className="space-y-2">
        {transactions.slice(0, 20).map((tx, i) => (
          <div key={i} className="card p-3 flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${
              tx.payment_status === 'completed' ? 'bg-green-500' : 
              tx.payment_status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
            }`} />
            <div className="flex-1">
              <p className="text-sm font-medium">{tx.plan} - {tx.amount}€</p>
              <p className="text-xs text-muted-foreground">{tx.user_id}</p>
            </div>
            <span className="text-xs text-muted-foreground">
              {new Date(tx.created_at).toLocaleDateString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CatalogsSection({ catalogs, token, setCatalogs }) {
  const grouped = catalogs.reduce((acc, cat) => {
    if (!acc[cat.type]) acc[cat.type] = [];
    acc[cat.type].push(cat);
    return acc;
  }, {});

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Catalogues</h2>

      {Object.entries(grouped).map(([type, items]) => (
        <div key={type} className="mb-6">
          <h3 className="font-bold text-secondary mb-2 uppercase text-sm">{type}</h3>
          <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
            {items.map(item => (
              <div key={item.catalog_id} className="card p-2 text-center">
                <div 
                  className="w-8 h-8 rounded-lg mx-auto mb-1"
                  style={{ backgroundColor: item.hex_color || '#888' }}
                />
                <p className="text-[10px] font-bold truncate">{item.code}</p>
                <p className="text-[9px] text-muted-foreground truncate">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      {catalogs.length === 0 && (
        <p className="text-muted-foreground text-center py-10">Aucun catalogue personnalisé</p>
      )}
    </div>
  );
}

function SettingsSection({ settings, token, setSettings }) {
  const [localSettings, setLocalSettings] = useState(settings || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalSettings(settings || {});
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(localSettings)
      });
      if (res.ok) {
        toast.success('Paramètres sauvegardés');
        setSettings(localSettings);
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Paramètres système</h2>

      <div className="space-y-4 max-w-md">
        <div>
          <label className="text-sm font-medium block mb-1">Provider IA par défaut</label>
          <select
            value={localSettings.ai_provider || 'gemini'}
            onChange={(e) => setLocalSettings({ ...localSettings, ai_provider: e.target.value })}
            className="w-full input"
          >
            <option value="gemini">Gemini (Nano Banana)</option>
            <option value="openai">OpenAI (GPT-4o)</option>
          </select>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Crédits par défaut (nouveaux utilisateurs)</label>
          <input
            type="number"
            value={localSettings.default_credits || 3}
            onChange={(e) => setLocalSettings({ ...localSettings, default_credits: parseInt(e.target.value) })}
            className="w-full input"
          />
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Jours d'essai</label>
          <input
            type="number"
            value={localSettings.trial_days || 7}
            onChange={(e) => setLocalSettings({ ...localSettings, trial_days: parseInt(e.target.value) })}
            className="w-full input"
          />
        </div>

        <div className="flex items-center justify-between py-2">
          <span className="text-sm">Mode maintenance</span>
          <button
            onClick={() => setLocalSettings({ ...localSettings, maintenance_mode: !localSettings.maintenance_mode })}
            className={`w-12 h-7 rounded-full transition-colors ${
              localSettings.maintenance_mode ? 'bg-red-500' : 'bg-white/10'
            }`}
          >
            <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
              localSettings.maintenance_mode ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        <div className="border border-dashed border-violet-500/50 rounded-lg p-3 opacity-60">
          <p className="text-violet-400 text-xs font-bold mb-2">À VENIR</p>
          <div className="space-y-2 text-sm text-violet-300">
            <p>• Configuration Stripe (clés API)</p>
            <p>• Limites de rate limiting</p>
            <p>• Webhooks personnalisés</p>
            <p>• Backup automatique</p>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full btn-primary disabled:opacity-50"
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      </div>
    </div>
  );
}

function LogsSection({ logs }) {
  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Logs IA ({logs.length})</h2>

      <div className="space-y-2">
        {logs.map((log, i) => (
          <div key={i} className="card p-3 flex items-center gap-3">
            <Database size={16} className="text-secondary" />
            <div className="flex-1">
              <p className="text-sm">{log.object_type} - {log.provider}</p>
              <p className="text-xs text-muted-foreground">{log.user_id}</p>
            </div>
            <span className="text-xs text-muted-foreground">
              {new Date(log.timestamp).toLocaleString()}
            </span>
          </div>
        ))}
      </div>

      {logs.length === 0 && (
        <p className="text-muted-foreground text-center py-10">Aucun log</p>
      )}
    </div>
  );
}

function AdminLogsSection({ logs }) {
  const actionLabels = {
    'delete_user': { label: 'Suppression utilisateur', color: '#EF4444', icon: Trash2 },
    'send_reset_password': { label: 'Envoi reset mot de passe', color: '#3B82F6', icon: Key },
    'verify_email': { label: 'Validation email', color: '#22C55E', icon: Check },
    'block_user': { label: 'Blocage utilisateur', color: '#F59E0B', icon: Lock },
    'unblock_user': { label: 'Déblocage utilisateur', color: '#10B981', icon: Unlock },
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Logs Admin ({logs.length})</h2>

      <div className="space-y-2">
        {logs.map((log, i) => {
          const actionConfig = actionLabels[log.action] || { label: log.action, color: '#64748B', icon: FileText };
          const Icon = actionConfig.icon;

          return (
            <div key={i} className="card p-3">
              <div className="flex items-start gap-3">
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${actionConfig.color}20` }}
                >
                  <Icon size={16} style={{ color: actionConfig.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm" style={{ color: actionConfig.color }}>
                    {actionConfig.label}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Par: {log.admin_email || log.admin_id}
                  </p>
                  {log.target_email && (
                    <p className="text-xs text-muted-foreground">
                      Cible: {log.target_email}
                    </p>
                  )}
                  {log.reason && (
                    <p className="text-xs text-yellow-500 mt-1">
                      Raison: {log.reason}
                    </p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {logs.length === 0 && (
        <p className="text-muted-foreground text-center py-10">Aucun log admin</p>
      )}
    </div>
  );
}

function GenerationsSection({ generations, token, setGenerations }) {
  const [filters, setFilters] = useState({
    user_id: '',
    object_type: '',
    date_from: '',
    date_to: ''
  });
  const [selectedGen, setSelectedGen] = useState(null);
  const [loading, setLoading] = useState(false);

  const objectTypes = [
    { value: '', label: 'Tous les types' },
    { value: 'store', label: 'Store banne' },
    { value: 'pergola', label: 'Pergola' },

  ];

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '50');
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.object_type) params.append('object_type', filters.object_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const res = await fetch(`${API_URL}/api/admin/generations?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        setGenerations(await res.json());
      }
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (imageUrl, filename) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename || 'generation.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Générations IA ({generations.total})</h2>
      </div>

      <div className="card p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-secondary" />
          <span className="font-medium text-sm">Filtres</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">User ID</label>
            <input
              type="text"
              placeholder="user_xxxxx"
              value={filters.user_id}
              onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
              className="w-full input text-sm"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Type d'objet</label>
            <select
              value={filters.object_type}
              onChange={(e) => setFilters({ ...filters, object_type: e.target.value })}
              className="w-full input text-sm"
            >
              {objectTypes.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Date début</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="w-full input text-sm"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Date fin</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="w-full input text-sm"
            />
          </div>
        </div>

        <button
          onClick={applyFilters}
          disabled={loading}
          className="mt-3 px-4 py-2 bg-secondary/20 text-secondary rounded-lg text-sm font-medium hover:bg-secondary/30 disabled:opacity-50"
        >
          {loading ? 'Chargement...' : 'Appliquer les filtres'}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {generations.generations?.map((gen, i) => (
          <motion.div
            key={gen.generation_id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.03 }}
            className="group cursor-pointer"
            onClick={async () => {
              setSelectedGen({ ...gen, _loading: true });
              try {
                const res = await fetch(`${API_URL}/api/admin/generations/${gen.generation_id}`, {
                  headers: { 'Authorization': `Bearer ${token}` },
                  credentials: 'include'
                });
                if (res.ok) setSelectedGen(await res.json());
                else setSelectedGen(gen);
              } catch { setSelectedGen(gen); }
            }}
          >
            <div className="aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-secondary/50 transition-all relative">
              <img
                src={gen.image_url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-2 left-2 right-2">
                  <p className="text-xs font-bold truncate">{gen.user_name || gen.user_id}</p>
                  <p className="text-[10px] text-muted-foreground">{gen.object_type}</p>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {generations.generations?.length === 0 && (
        <p className="text-muted-foreground text-center py-10">Aucune génération</p>
      )}

      <AnimatePresence>
        {selectedGen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setSelectedGen(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-light w-full max-w-5xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <Sparkles className="text-secondary" size={20} />
                  <div>
                    <h3 className="font-bold">Détail génération</h3>
                    <p className="text-xs text-muted-foreground">{selectedGen.generation_id}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedGen(null)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto flex">
                <div className="w-2/3 p-4 bg-black/20">
                  {selectedGen._loading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-muted-foreground text-sm">Chargement des détails...</div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Generated image */}
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Image générée</p>
                        <img
                          src={selectedGen.image_url}
                          alt=""
                          className="w-full h-auto object-contain rounded-lg"
                        />
                      </div>

                      {/* Original & Measured photos side by side */}
                      {(selectedGen.original_photo_base64 || selectedGen.measured_photo_base64) && (
                        <div className="grid grid-cols-2 gap-3">
                          {selectedGen.original_photo_base64 && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Photo originale</p>
                              <img
                                src={selectedGen.original_photo_base64}
                                alt=""
                                className="w-full h-auto object-contain rounded-lg border border-white/10"
                              />
                            </div>
                          )}
                          {selectedGen.measured_photo_base64 && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Photo avec mesure</p>
                              <img
                                src={selectedGen.measured_photo_base64}
                                alt=""
                                className="w-full h-auto object-contain rounded-lg border border-white/10"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="w-1/3 p-4 border-l border-white/10 overflow-y-auto">
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Utilisateur</p>
                      <p className="font-medium">{selectedGen.user_name || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground">{selectedGen.user_email}</p>
                      <p className="text-[10px] text-muted-foreground">{selectedGen.user_id}</p>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Type d'objet</p>
                      <span className="px-2 py-1 rounded-lg bg-secondary/20 text-secondary text-sm font-medium">
                        {selectedGen.object_type}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Date de création</p>
                      <p className="text-sm">{new Date(selectedGen.created_at).toLocaleString()}</p>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Provider</p>
                      <p className="text-sm">{selectedGen.provider}</p>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Modèle IA</p>
                      <p className="text-sm font-medium">{selectedGen.model || 'unknown'}</p>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Configuration</p>
                      <div className="bg-black/20 rounded-lg p-2 text-xs font-mono overflow-x-auto">
                        <pre>{JSON.stringify(selectedGen.config, null, 2)}</pre>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Prompt utilisé</p>
                      <p className="text-xs bg-black/20 rounded-lg p-2 whitespace-pre-wrap">
                        {selectedGen.prompt}
                      </p>
                    </div>

                    <button
                      onClick={() => downloadImage(selectedGen.image_url, `generation_${selectedGen.generation_id}.png`)}
                      className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
                    >
                      <Download size={16} />
                      <span className="text-sm font-medium">Télécharger l'image</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ConfigSection({ schema, values, setValues, token }) {
  const [activeTab, setActiveTab] = useState(Object.keys(schema)[0]);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});

  const iconMap = {
    'Settings': Settings,
    'Users': Users,
    'CreditCard': CreditCard,
    'Sparkles': Sparkles,
    'Shield': Shield,
    'Sun': Sun,
    'Home': Home
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const handleChange = (key, value) => {
    setPendingChanges(prev => ({ ...prev, [key]: value }));
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(pendingChanges)
      });

      if (res.ok) {
        const data = await res.json();
        setValues(data.config);
        setPendingChanges({});
        toast.success('Configuration sauvegardée');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const resetParam = async (key) => {
    setResetting(key);
    try {
      const res = await fetch(`${API_URL}/api/admin/config/reset/${key}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setValues(prev => ({ ...prev, [key]: data.value }));
        setPendingChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[key];
          return newChanges;
        });
        toast.success(`${key} réinitialisé`);
      }
    } catch {
      toast.error('Erreur');
    } finally {
      setResetting(null);
    }
  };

  const resetAll = async () => {
    if (!window.confirm('Réinitialiser TOUTE la configuration aux valeurs par défaut ?')) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/config/reset-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setValues(data.config);
        setPendingChanges({});
        toast.success('Configuration réinitialisée');
      }
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const getCurrentValue = (key) => {
    if (key in pendingChanges) return pendingChanges[key];
    return values[key];
  };

  const getDefaultValue = (categoryKey, paramKey) => {
    return schema[categoryKey]?.params[paramKey]?.default;
  };

  const isModified = (key) => {
    return key in pendingChanges;
  };

  const isDifferentFromDefault = (categoryKey, paramKey) => {
    const current = getCurrentValue(paramKey);
    const defaultVal = getDefaultValue(categoryKey, paramKey);
    return current !== defaultVal;
  };

  const renderInput = (categoryKey, paramKey, paramDef) => {
    const value = getCurrentValue(paramKey);
    const defaultVal = paramDef.default;
    const modified = isModified(paramKey);
    const differentFromDefault = isDifferentFromDefault(categoryKey, paramKey);

    const baseClass = `w-full ${modified ? 'ring-2 ring-secondary' : ''}`;

    return (
      <div className="flex items-center gap-2">
        {paramDef.type === 'string' && (
          <input
            type="text"
            value={value ?? ''}
            onChange={(e) => handleChange(paramKey, e.target.value)}
            className={`input text-sm flex-1 ${baseClass}`}
          />
        )}

        {paramDef.type === 'number' && (
          <input
            type="number"
            value={value ?? 0}
            min={paramDef.min}
            max={paramDef.max}
            step={paramDef.step || 1}
            onChange={(e) => handleChange(paramKey, parseFloat(e.target.value))}
            className={`input text-sm w-32 ${baseClass}`}
          />
        )}

        {paramDef.type === 'boolean' && (
          <button
            onClick={() => handleChange(paramKey, !value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              value 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-red-500/20 text-red-400'
            } ${modified ? 'ring-2 ring-secondary' : ''}`}
          >
            {value ? 'Activé' : 'Désactivé'}
          </button>
        )}

        {paramDef.type === 'select' && (
          <select
            value={value ?? ''}
            onChange={(e) => {
              const val = paramDef.options.includes(parseInt(e.target.value)) 
                ? parseInt(e.target.value) 
                : e.target.value;
              handleChange(paramKey, val);
            }}
            className={`input text-sm flex-1 ${baseClass}`}
          >
            {paramDef.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        )}

        {differentFromDefault && (
          <button
            onClick={() => resetParam(paramKey)}
            disabled={resetting === paramKey}
            className="p-2 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-white"
            title={`Défaut: ${defaultVal}`}
          >
            {resetting === paramKey ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Configuration</h2>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-xs text-secondary px-2 py-1 rounded bg-secondary/20">
              {Object.keys(pendingChanges).length} modification(s)
            </span>
          )}
          <button
            onClick={resetAll}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30"
          >
            Reset tout
          </button>
          <button
            onClick={saveChanges}
            disabled={!hasChanges || saving}
            className="px-4 py-1.5 rounded-lg text-sm font-medium bg-secondary text-white disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 border-b border-white/10 pb-4">
        {Object.entries(schema).map(([key, category]) => {
          const Icon = iconMap[category.icon] || Settings;
          const hasModified = Object.keys(category.params).some(pk => isModified(pk));

          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                activeTab === key 
                  ? 'bg-secondary/20 text-secondary' 
                  : 'hover:bg-white/5 text-muted-foreground'
              }`}
            >
              <Icon size={16} />
              <span>{category.label}</span>
              {hasModified && (
                <span className="w-2 h-2 rounded-full bg-secondary"></span>
              )}
            </button>
          );
        })}
      </div>

      {schema[activeTab] && (
        <div className="space-y-3">
          {Object.entries(schema[activeTab].params).map(([paramKey, paramDef]) => (
            <div key={paramKey} className="card p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{paramDef.label}</p>
                    {isDifferentFromDefault(activeTab, paramKey) && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                        Modifié
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{paramDef.description}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Défaut: <code className="bg-white/10 px-1 rounded">{String(paramDef.default)}</code>
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {renderInput(activeTab, paramKey, paramDef)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ObjectsSection({ objects, setObjects, token }) {
  const [expandedObject, setExpandedObject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const fileInputRef = React.useRef(null);

  const iconMap = {
    'Sun': Sun,
    'Home': Home
  };

  const sortedObjects = Object.entries(objects)
    .sort(([, a], [, b]) => (a.order || 99) - (b.order || 99));

  const toggleEnabled = async (objectId) => {
    setSaving(true);
    try {
      const newEnabled = !objects[objectId]?.enabled;
      const res = await fetch(`${API_URL}/api/admin/objects/${objectId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: newEnabled })
      });

      if (res.ok) {
        setObjects(prev => ({
          ...prev,
          [objectId]: { ...prev[objectId], enabled: newEnabled }
        }));
        toast.success(`${objects[objectId]?.label} ${newEnabled ? 'activé' : 'désactivé'}`);
      }
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const updateObjectParam = async (objectId, paramKey, value) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/objects/${objectId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ params: { [paramKey]: value } })
      });

      if (res.ok) {
        const data = await res.json();
        setObjects(prev => ({
          ...prev,
          [objectId]: data.object
        }));
      }
    } catch {
      toast.error('Erreur de mise à jour');
    }
  };

  const resetObject = async (objectId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/objects/${objectId}/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setObjects(prev => ({
          ...prev,
          [objectId]: data.object
        }));
        toast.success('Objet réinitialisé');
      }
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (e, objectId) => {
    setDraggedItem(objectId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetId) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;

    const items = [...sortedObjects];
    const draggedIndex = items.findIndex(([id]) => id === draggedItem);
    const targetIndex = items.findIndex(([id]) => id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const [removed] = items.splice(draggedIndex, 1);
    items.splice(targetIndex, 0, removed);

    const newOrders = {};
    items.forEach(([id], idx) => {
      newOrders[id] = idx + 1;
    });

    const newObjects = { ...objects };
    Object.entries(newOrders).forEach(([id, order]) => {
      if (newObjects[id]) newObjects[id].order = order;
    });
    setObjects(newObjects);

    try {
      await fetch(`${API_URL}/api/admin/objects/reorder`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newOrders)
      });
      toast.success('Ordre mis à jour');
    } catch {
      toast.error('Erreur de réordonnancement');
    }

    setDraggedItem(null);
  };

  const exportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/objects/export`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'soview_objects_config.csv';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Export CSV téléchargé');
      }
    } catch {
      toast.error('Erreur d\'export');
    }
  };

  const importCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const res = await fetch(`${API_URL}/api/admin/objects/import`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'text/csv' },
          credentials: 'include',
          body: event.target.result
        });

        if (res.ok) {
          const data = await res.json();
          setObjects(data.objects);
          toast.success('Import CSV réussi');
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Erreur d\'import');
        }
      } catch {
        toast.error('Erreur d\'import');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const resetAll = async () => {
    if (!window.confirm('Réinitialiser TOUS les objets aux valeurs par défaut ?')) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/objects/reset-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setObjects(data.objects);
        toast.success('Tous les objets réinitialisés');
      }
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Objets Configurables</h2>
          <p className="text-xs text-muted-foreground">Glissez-déposez pour réordonner</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={importCSV}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
          >
            <Upload size={14} />
            Import CSV
          </button>
          <button
            onClick={exportCSV}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500/20 text-green-400 hover:bg-green-500/30"
          >
            <Download size={14} />
            Export CSV
          </button>
          <button
            onClick={resetAll}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30"
          >
            Reset tout
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {sortedObjects.map(([objectId, objData]) => {
          const Icon = iconMap[objData.icon] || Box;
          const isExpanded = expandedObject === objectId;

          return (
            <div
              key={objectId}
              draggable
              onDragStart={(e) => handleDragStart(e, objectId)}
              onDragOver={(e) => handleDragOver(e, objectId)}
              onDrop={(e) => handleDrop(e, objectId)}
              className={`card transition-all ${draggedItem === objectId ? 'opacity-50' : ''} ${!objData.enabled ? 'opacity-60' : ''}`}
            >
              <div className="p-4 flex items-center gap-3">
                <div className="cursor-grab text-muted-foreground hover:text-white">
                  <GripVertical size={18} />
                </div>

                <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                  <Icon size={20} className="text-secondary" />
                </div>

                <div className="flex-1">
                  <p className="font-bold">{objData.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {Object.keys(objData.params || {}).length} paramètres
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">#{objData.order}</span>

                  <button
                    onClick={() => toggleEnabled(objectId)}
                    disabled={saving}
                    className={`p-1 rounded transition-colors ${objData.enabled ? 'text-green-400' : 'text-red-400'}`}
                    data-testid={`toggle-${objectId}`}
                  >
                    {objData.enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>

                  <button
                    onClick={() => setExpandedObject(isExpanded ? null : objectId)}
                    className="p-2 rounded-lg hover:bg-white/10"
                  >
                    <ChevronRight size={18} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-white/10 overflow-hidden"
                  >
                    <div className="p-4 space-y-3">
                      {Object.entries(objData.params || {}).map(([paramKey, paramDef]) => {
                        const currentValue = paramDef.value ?? paramDef.default;

                        return (
                          <div key={paramKey} className="flex items-center justify-between gap-4">
                            <div>
                              <p className="text-sm font-medium">{paramDef.label}</p>
                              <p className="text-[10px] text-muted-foreground">
                                Défaut: {String(paramDef.default)}
                              </p>
                            </div>

                            <div className="flex items-center gap-2">
                              {paramDef.type === 'number' && (
                                <input
                                  type="number"
                                  value={currentValue}
                                  min={paramDef.min}
                                  max={paramDef.max}
                                  onChange={(e) => updateObjectParam(objectId, paramKey, parseFloat(e.target.value))}
                                  className="input w-24 text-sm"
                                />
                              )}

                              {paramDef.type === 'string' && (
                                <input
                                  type="text"
                                  value={currentValue}
                                  onChange={(e) => updateObjectParam(objectId, paramKey, e.target.value)}
                                  className="input w-32 text-sm"
                                />
                              )}

                              {paramDef.type === 'boolean' && (
                                <button
                                  onClick={() => updateObjectParam(objectId, paramKey, !currentValue)}
                                  className={`px-3 py-1 rounded-lg text-xs font-medium ${
                                    currentValue ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                  }`}
                                >
                                  {currentValue ? 'Oui' : 'Non'}
                                </button>
                              )}

                              {paramDef.type === 'select' && (
                                <select
                                  value={currentValue}
                                  onChange={(e) => {
                                    const val = paramDef.options.includes(parseInt(e.target.value))
                                      ? parseInt(e.target.value)
                                      : e.target.value;
                                    updateObjectParam(objectId, paramKey, val);
                                  }}
                                  className="input text-sm"
                                >
                                  {paramDef.options.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      <div className="pt-2 border-t border-white/10">
                        <button
                          onClick={() => resetObject(objectId)}
                          className="text-xs text-muted-foreground hover:text-red-400 flex items-center gap-1"
                        >
                          <RefreshCw size={12} />
                          Réinitialiser cet objet
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AICostsSection({ summary, byModel, byUser, byMonth, details, pricingConfig }) {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble' },
    { id: 'models', label: 'Par modèle' },
    { id: 'users', label: 'Par utilisateur' },
    { id: 'months', label: 'Par mois' },
    { id: 'details', label: 'Détails' },
    { id: 'pricing', label: 'Tarifs' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4">
        <h2 className="text-xl font-bold">Coûts IA (OpenRouter)</h2>
        <div className="flex flex-wrap gap-1 bg-white/5 rounded-lg p-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                activeTab === tab.id ? 'bg-teal-600 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="card p-4">
              <p className="text-2xl font-bold text-teal-400">${summary.total_cost_usd?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-muted-foreground">Coût total</p>
            </div>
            <div className="card p-4">
              <p className="text-2xl font-bold text-blue-400">${summary.current_month_cost_usd?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-muted-foreground">Ce mois-ci ({summary.current_month || '-'})</p>
            </div>
            <div className="card p-4">
              <p className="text-2xl font-bold text-purple-400">{summary.current_month_generations || 0}</p>
              <p className="text-xs text-muted-foreground">Générations ce mois</p>
            </div>
            <div className="card p-4">
              <p className="text-2xl font-bold text-amber-400">{summary.current_month_users || 0}</p>
              <p className="text-xs text-muted-foreground">Utilisateurs actifs</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="card p-4">
              <p className="text-sm font-bold mb-2">Scores clés</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total générations</span>
                  <span className="font-bold">{summary.total_generations || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total tokens</span>
                  <span className="font-bold">{(summary.total_tokens || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tokens ce mois</span>
                  <span className="font-bold">{(summary.current_month_tokens || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Coût moyen / génération</span>
                  <span className="font-bold">
                    ${summary.total_generations > 0 ? (summary.total_cost_usd / summary.total_generations).toFixed(4) : '0.0000'}
                  </span>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <p className="text-sm font-bold mb-2">Modèles utilisés ce mois</p>
              {byModel.filter(m => m.monthly?.some(mm => mm.month === summary.current_month)).length > 0 ? (
                <div className="space-y-2">
                  {byModel
                    .filter(m => m.monthly?.some(mm => mm.month === summary.current_month))
                    .slice(0, 5)
                    .map((model, i) => {
                      const thisMonth = model.monthly.find(mm => mm.month === summary.current_month);
                      return (
                        <div key={i} className="flex items-center justify-between text-sm gap-2">
                          <span className="text-xs truncate" title={model.model}>{model.model}</span>
                          <span className="font-bold text-teal-400">${thisMonth?.cost?.toFixed(4) || '0.0000'}</span>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Aucune donnée ce mois</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'models' && (
        <div className="space-y-3">
          {byModel.length === 0 ? (
            <p className="text-muted-foreground">Aucune donnée disponible</p>
          ) : byModel.map((model, i) => (
            <div key={i} className="card p-4">
              <p className="font-bold text-sm">{model.model}</p>
              <p className="text-xs text-muted-foreground mb-2">
                ${model.total_cost?.toFixed(2) || '0.00'} total · {model.total_generations} générations · {(model.total_tokens || 0).toLocaleString()} tokens
              </p>
              <div className="space-y-1">
                {model.monthly?.map((month, mi) => (
                  <div key={mi} className="grid grid-cols-5 gap-2 text-xs bg-white/5 rounded px-2 py-1">
                    <span>{month.month}</span>
                    <span className="text-teal-400 font-bold">${month.cost?.toFixed(4)}</span>
                    <span>{month.generations} gén.</span>
                    <span>{month.tokens?.toLocaleString()} tokens</span>
                    <span>{month.users} users</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'users' && (
        <CostTable
          empty="Aucune donnée disponible"
          columns={['Utilisateur', 'Coût total', 'Générations', 'Tokens']}
          rows={byUser.map(item => [
            item.user_id,
            `$${item.total_cost_usd?.toFixed(2) || '0.00'}`,
            item.total_generations,
            (item.total_tokens || 0).toLocaleString(),
          ])}
        />
      )}

      {activeTab === 'months' && (
        <CostTable
          empty="Aucune donnée disponible"
          columns={['Mois', 'Coût total', 'Générations', 'Tokens', 'Modèles', 'Utilisateurs']}
          rows={byMonth.map(item => [
            item.month,
            `$${item.total_cost_usd?.toFixed(2) || '0.00'}`,
            item.total_generations,
            (item.total_tokens || 0).toLocaleString(),
            item.model_count,
            item.user_count,
          ])}
        />
      )}

      {activeTab === 'details' && (
        <CostTable
          empty="Aucun détail disponible"
          columns={['Date', 'Utilisateur', 'Modèle', 'Source', 'Tokens', 'Coût']}
          rows={(details.records || []).map(item => [
            item.created_at ? new Date(item.created_at).toLocaleString() : '-',
            item.user_id,
            item.model,
            item.source,
            (item.total_tokens || 0).toLocaleString(),
            `$${item.cost_usd?.toFixed(4) || '0.0000'}`,
          ])}
        />
      )}

      {activeTab === 'pricing' && pricingConfig && (
        <div>
          <p className="text-xs text-muted-foreground mb-3">
            Tarifs en USD par million de tokens. Ces prix sont basés sur les tarifs OpenRouter.
          </p>
          <CostTable
            empty="Aucun tarif configuré"
            columns={['Modèle', 'Prompt / 1M', 'Completion / 1M']}
            rows={Object.entries(pricingConfig.models || {}).map(([model, pricing]) => [
              model,
              `$${pricing.prompt_price_per_1m?.toFixed(2) || '0.00'}`,
              `$${pricing.completion_price_per_1m?.toFixed(2) || '0.00'}`,
            ])}
          />
          {pricingConfig.default_fallback && (
            <p className="text-xs text-muted-foreground mt-2">
              Fallback par défaut: ${pricingConfig.default_fallback.prompt_price_per_1m}/M in, ${pricingConfig.default_fallback.completion_price_per_1m}/M out
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CostTable({ columns, rows, empty }) {
  if (rows.length === 0) {
    return <p className="text-muted-foreground">{empty}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {columns.map(column => (
              <th key={column} className="text-left py-2 px-2 text-muted-foreground font-medium">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-white/5 hover:bg-white/5">
              {row.map((value, colIndex) => (
                <td key={colIndex} className="py-2 px-2 text-xs">
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PromptsSection({ promptsData, setPromptsData, token }) {
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const { prompts, schema } = promptsData;

  const startEdit = (key) => {
    setEditingPrompt(key);
    setEditValue(
      typeof prompts[key]?.current === 'object' 
        ? JSON.stringify(prompts[key]?.current, null, 2)
        : prompts[key]?.current || ''
    );
    setPreview(null);
  };

  const cancelEdit = () => {
    setEditingPrompt(null);
    setEditValue('');
    setPreview(null);
  };

  const savePrompt = async (key) => {
    setSaving(true);
    try {
      let valueToSave = editValue;

      if (schema[key]?.type === 'object_map') {
        try {
          valueToSave = JSON.parse(editValue);
        } catch {
          toast.error('JSON invalide');
          setSaving(false);
          return;
        }
      }

      const res = await fetch(`${API_URL}/api/admin/prompts/${key}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: valueToSave })
      });

      if (res.ok) {
        const refreshRes = await fetch(`${API_URL}/api/admin/prompts`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (refreshRes.ok) {
          setPromptsData(await refreshRes.json());
        }
        setEditingPrompt(null);
        toast.success('Prompt mis à jour');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const resetPrompt = async (key) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/prompts/${key}/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const refreshRes = await fetch(`${API_URL}/api/admin/prompts`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (refreshRes.ok) {
          setPromptsData(await refreshRes.json());
        }
        toast.success('Prompt réinitialisé');
      }
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm('Réinitialiser TOUS les prompts aux valeurs par défaut ?')) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/prompts/reset-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const refreshRes = await fetch(`${API_URL}/api/admin/prompts`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (refreshRes.ok) {
          setPromptsData(await refreshRes.json());
        }
        toast.success('Tous les prompts réinitialisés');
      }
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const previewPrompt = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/prompts/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ template: editValue })
      });

      if (res.ok) {
        const data = await res.json();
        setPreview(data);
      }
    } catch {
      toast.error('Erreur de prévisualisation');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Configuration des Prompts IA</h2>
          <p className="text-xs text-muted-foreground">Personnalisez les prompts utilisés pour la génération d'images</p>
        </div>
        <button
          onClick={resetAll}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30"
        >
          Reset tout
        </button>
      </div>

      <div className="card p-4 mb-4 bg-blue-500/5 border-blue-500/20">
        <div className="flex items-center gap-2 mb-2">
          <Database size={16} className="text-blue-400" />
          <span className="font-medium text-blue-400 text-sm">Variables disponibles</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <code className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs">{'{obj_name}'}</code>
          <code className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs">{'{dims_str}'}</code>
          <code className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs">{'{colors_str}'}</code>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">
          Ces variables sont automatiquement remplacées lors de la génération
        </p>
      </div>

      <div className="space-y-3">
        {Object.entries(schema).map(([key, schemaDef]) => {
          const promptData = prompts[key] || {};
          const isEditing = editingPrompt === key;

          return (
            <div key={key} className="card overflow-hidden">
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-bold">{schemaDef.label}</p>
                    {promptData.is_modified && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                        Modifié
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{schemaDef.description}</p>

                  {schemaDef.variables?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {schemaDef.variables.map(v => (
                        <span key={v.name} className="text-[9px] px-1.5 py-0.5 rounded bg-secondary/20 text-secondary" title={v.description}>
                          {'{' + v.name + '}'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {promptData.is_modified && (
                    <button
                      onClick={() => resetPrompt(key)}
                      disabled={saving}
                      className="p-2 rounded-lg hover:bg-white/10 text-muted-foreground"
                      title="Réinitialiser"
                    >
                      <RefreshCw size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => isEditing ? cancelEdit() : startEdit(key)}
                    className="p-2 rounded-lg hover:bg-white/10"
                  >
                    <Edit size={14} />
                  </button>
                </div>
              </div>

              {!isEditing && (
                <div className="px-4 pb-4">
                  <div className="bg-black/20 rounded-lg p-3 text-xs font-mono whitespace-pre-wrap max-h-32 overflow-y-auto">
                    {typeof promptData.current === 'object' 
                      ? JSON.stringify(promptData.current, null, 2)
                      : promptData.current}
                  </div>
                </div>
              )}

              <AnimatePresence>
                {isEditing && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-white/10"
                  >
                    <div className="p-4 space-y-3">
                      {key === 'image_edit_models' ? (
                        <div className="space-y-3">
                          {/* Available models info box */}
                          <div className="card p-3 bg-purple-500/5 border-purple-500/20">
                            <p className="text-xs font-medium text-purple-400 mb-2">Modèles disponibles sur OpenRouter :</p>
                            <div className="flex flex-wrap gap-1">
                              {[
                                'google/gemini-2.5-flash-image-preview',
                                'google/gemini-3.1-flash-image-preview',
                                'google/gemini-2.5-flash-image',
                                'openai/gpt-image-2',
                                'google/gemini-3-pro-image-preview',
                              ].map(model => (
                                <button
                                  key={model}
                                  onClick={() => {
                                    const models = editValue.split(',').filter(m => m.trim());
                                    if (!models.includes(model)) {
                                      models.push(model);
                                      setEditValue(models.join(','));
                                    }
                                  }}
                                  className="text-xs px-2 py-1 rounded bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 text-purple-300"
                                >
                                  + {model.split('/').pop()}
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Current models as removable chips */}
                          <div className="flex flex-wrap gap-2">
                            {editValue.split(',').filter(m => m.trim()).map((model, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-sm">
                                <span className="text-blue-300">{model.trim()}</span>
                                <button
                                  onClick={() => {
                                    const models = editValue.split(',').filter(m => m.trim());
                                    models.splice(idx, 1);
                                    setEditValue(models.join(','));
                                  }}
                                  className="text-blue-400 hover:text-red-400 ml-1 text-xs"
                                >×</button>
                              </span>
                            ))}
                          </div>
                          {/* Add model input */}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Ajouter un modèle (ex: google/gemini-2.5-flash-image)"
                              className="flex-1 bg-background border rounded px-3 py-2 text-sm"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.target.value.trim()) {
                                  const models = editValue.split(',').filter(m => m.trim());
                                  models.push(e.target.value.trim());
                                  setEditValue(models.join(','));
                                  e.target.value = '';
                                }
                              }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Appuyez Entrée pour ajouter. L'ordre définit la priorité (le premier modèle est essayé en premier).
                          </p>
                        </div>
                      ) : schemaDef.type === 'object_map' ? (
                        <textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-full h-48 bg-black/30 border border-white/10 rounded-lg p-3 text-xs font-mono resize-none focus:outline-none focus:border-secondary"
                          placeholder="JSON object..."
                        />
                      ) : (
                        <textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-full h-32 bg-black/30 border border-white/10 rounded-lg p-3 text-sm resize-none focus:outline-none focus:border-secondary"
                          placeholder="Entrez le prompt..."
                        />
                      )}

                      {key === 'generation_prompt' && (
                        <div>
                          <button
                            onClick={previewPrompt}
                            className="text-xs text-secondary hover:underline mb-2"
                          >
                            Prévisualiser avec des valeurs d'exemple
                          </button>
                          {preview && (
                            <div className={`rounded-lg p-3 text-xs ${preview.error ? 'bg-red-500/20 text-red-400' : 'bg-green-500/10 text-green-400'}`}>
                              {preview.error || preview.preview}
                            </div>
                          )}
                        </div>
                      )}

                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground hover:text-white">
                          Voir la valeur par défaut
                        </summary>
                        <div className="mt-2 bg-black/20 rounded-lg p-2 font-mono whitespace-pre-wrap">
                          {typeof promptData.default === 'object' 
                            ? JSON.stringify(promptData.default, null, 2)
                            : promptData.default}
                        </div>
                      </details>

                      <div className="flex justify-end gap-2">
                        <button
                          onClick={cancelEdit}
                          className="px-3 py-1.5 rounded-lg text-sm bg-white/10 hover:bg-white/20"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={() => savePrompt(key)}
                          disabled={saving}
                          className="px-4 py-1.5 rounded-lg text-sm bg-secondary text-white font-medium disabled:opacity-50"
                        >
                          {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
